// Centralised legal copy for OFinancial Markets. Update content here, not the page.
// Source: copy supplied by Abdul on 24 May 2026 · DO NOT paraphrase.
//
// Schema:
//   sections: [
//     {
//       h: "Heading",
//       body: [
//         "Paragraph string",
//         { list: ["bullet a", "bullet b"] },
//         "Another paragraph",
//         ...
//       ]
//     }
//   ]
const LAST_UPDATED = "January 1, 2026";

export const LEGAL = {
  /* ────────────────────────── TERMS ────────────────────────── */
  terms: {
    slug: "terms",
    title: "Terms & Conditions",
    intro:
      "This website, OFinancial Markets (referred to as “the Company”), provides information, tools, and services to users, subject to acceptance of all terms, conditions, policies, and notices outlined herein.",
    sections: [
      {
        h: "Summary of Terms & Conditions",
        body: [
          "By utilizing our site or making a purchase, you agree to abide by the Terms of Service, including any additional terms, conditions, and policies referenced within. These terms apply to all users, including browsers, vendors, customers, merchants, and content contributors.",
          "Before accessing or using our website, carefully review these Terms of Service. If you disagree with any part of this agreement, refrain from accessing the site or using its services. Access to the Services is only permitted for individuals over 18 years old residing in eligible countries.",
          "Users must confirm they are over 18 to register on the website. Access to and use of the Services must comply with applicable laws. Any additions to the platform will also be subject to these Terms of Service.",
          "The Terms of Service may be updated or changed at any time, with changes posted on the website. Users are responsible for regularly reviewing these terms. Continued use of the website after changes signifies acceptance of the updated terms.",
          "It is important to note that none of the services provided constitute investment services as per applicable laws. The Company does not offer guidance, instructions, or investment advice regarding transactions or financial instruments. Employees or representatives of the Company are not authorised to provide investment advice. Any information construed as investment advice is explicitly disclaimed by the Company.",
          "Access to third parties may be granted to troubleshoot and maintain website, database, or infrastructure issues. Such access is monitored and removed once the work scope is completed.",
          "All payments made are final and solely for evaluation purposes.",
          "Registration fees are paid for access to the OFinancial Markets platform, simulated trading environment, and related services. Refunds for registration fees are not provided if the service has commenced (i.e. trading activity has begun) and or if the user has not successfully completed the evaluation. No refunds are applicable for the services offered by OFinancial Markets.",
        ],
      },
      {
        h: "Section 1 – Online Registration Terms",
        body: [
          "By agreeing to these Terms of Service, you confirm that you are of legal age in your country of residence. You may not utilise our services for illegal or unauthorised purposes, nor violate any laws in your jurisdiction including intellectual property laws. Violation of any Terms will result in immediate termination of your Services.",
        ],
      },
      {
        h: "Section 2 – General Conditions",
        body: [
          "We reserve the right to refuse service to anyone at any time and for any reason. Your content excluding payment information may be transferred unencrypted over various networks. Payment information is always encrypted during transfer. You agree not to reproduce, duplicate, sell, resell, or exploit any part of the Service without express written permission from us. Headings used in this agreement are for convenience only and will not affect these Terms.",
        ],
      },
      {
        h: "Section 3 – Accuracy, Completeness, and Timeliness of Information",
        body: [
          "We strive to ensure the accuracy of the information provided, but it may also be sourced from third parties for which we are not responsible if it is not entirely accurate, complete, or current. The content on this site is for general information purposes only and should not be solely relied upon for decision making. Any reliance on the material provided is at your own risk. This site may contain historical information which is provided for reference only. We reserve the right to modify the contents of the site at any time without obligation to update information.",
        ],
      },
      {
        h: "Section 4 – Modifications to the Service and Prices",
        body: [
          "Prices and services are subject to change without prior notice. We reserve the right to modify or discontinue any part of the Service at any time. We shall not be liable for any modification, price change, suspension, or discontinuation of the Service.",
        ],
      },
      {
        h: "Section 5 – Services",
        body: [
          "We reserve the right to limit access to our services to any person, region, or jurisdiction at our discretion. Services may be modified, limited, or discontinued at any time. We do not guarantee that the quality of services will meet expectations or that errors will be corrected.",
        ],
      },
      {
        h: "Section 6 – Accuracy of Billing and Account Information",
        body: [
          "We reserve the right to refuse any order. We may limit or cancel quantities purchased per user or account. Users agree to provide accurate account and billing information and to update such information as necessary.",
        ],
      },
      {
        h: "Section 7 – Optional Tools",
        body: [
          "The platform may provide access to third party tools or integrations. These are provided without warranties and are used at your own risk. The Company is not liable for any issues arising from third party tools.",
        ],
      },
      {
        h: "Section 8 – Third party Links",
        body: [
          "Certain content, products, or services may include materials from third parties. We are not responsible for examining or evaluating third party content or accuracy and we do not warrant or assume liability for third party materials or websites.",
        ],
      },
      {
        h: "Section 9 – Personal Information",
        body: ["Submission of personal information is governed by our Privacy Policy."],
      },
      {
        h: "Section 10 – Prohibited Uses",
        body: [
          "In addition to other prohibitions, you are prohibited from using the site for unlawful, abusive, or deceptive purposes. This includes but is not limited to violation of intellectual property, spreading malicious software, or collecting personal data without consent.",
          "In relation to trading activity, the following are strictly prohibited:",
          {
            list: [
              "latency arbitrage",
              "hedging across multiple accounts",
              "copy trading from third parties",
              "coordinated or group trading",
              "strategy abuse designed to bypass evaluation rules",
            ],
          },
          "Violation of these rules may result in account termination, forfeiture of gains, and permanent restriction from the platform.",
        ],
      },
      {
        h: "Section 11 – Simulated Trading Environment",
        body: [
          "All trading activity on OFinancial Markets is conducted in a simulated environment using notional capital. No real funds are traded on behalf of the user. Performance results do not represent real trading outcomes.",
        ],
      },
      {
        h: "Section 12 – Payments",
        body: [
          "All payments are final and non refundable once access to the platform has been granted. Fees are charged for access to services and do not represent deposits or investments.",
        ],
      },
      {
        h: "Section 13 – Refunds",
        body: [
          "Refunds are not provided once the service has commenced or the user has accessed their account. The Company may review exceptional cases at its sole discretion.",
        ],
      },
      {
        h: "Section 14 – Withdrawals",
        body: [
          "Withdrawals are subject to successful identity verification, source-of-funds checks where applicable, and the Company's standard anti-money-laundering and counter-terrorism financing review. The Company reserves the right to delay or refuse a withdrawal request pending such review. Withdrawals are routed back to the original deposit method whenever possible.",
        ],
      },
      {
        h: "Section 15 – Disputes",
        body: [
          "Disputing a payment after accessing the service is considered a violation of these Terms. Accounts involved in payment disputes may be permanently banned. The Company reserves the right to refuse future services to users involved in disputes.",
        ],
      },
      {
        h: "Section 16 – Disclaimer of Warranties; Limitation of Liability",
        body: [
          "We do not guarantee uninterrupted or error free service. The Service is provided as is and at your own risk. In no event shall the Company be liable for any damages including loss of profits, revenue, or data.",
        ],
      },
      {
        h: "Section 17 – Indemnification",
        body: [
          "You agree to indemnify and hold harmless the Company and its affiliates from any claims arising from your use of the Service or violation of these Terms.",
        ],
      },
      {
        h: "Section 18 – Severability",
        body: [
          "If any provision is found to be unenforceable, the remaining provisions shall remain valid and enforceable.",
        ],
      },
      {
        h: "Section 19 – Termination",
        body: [
          "These Terms remain effective unless terminated by either party. We may terminate your access at any time if you violate these Terms.",
        ],
      },
      {
        h: "Section 20 – Entire Agreement",
        body: [
          "These Terms constitute the entire agreement between you and the Company and supersede any prior agreements.",
        ],
      },
      {
        h: "Section 21 – Changes to Terms",
        body: [
          "We reserve the right to update these Terms at any time. Continued use of the platform constitutes acceptance of any changes.",
        ],
      },
      {
        h: "Section 22 – Governing Law",
        body: ["These Terms are governed by the laws of Antigua and Barbuda."],
      },
      {
        h: "Section 23 – Contact Information",
        body: [
          "For any inquiries regarding these Terms, please contact:",
          {
            list: [
              "Company: OFinancial Markets",
              "Website: https://ofinancial.markets",
              "Email: support@ofinancial.markets",
            ],
          },
        ],
      },
    ],
  },

  /* ────────────────────────── PRIVACY ────────────────────────── */
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    intro:
      "This Privacy Notice for OFinancial Markets (\"OFinancial Markets\", \"we\", \"us\", or \"our\") explains how and why we collect, use, store, disclose, and otherwise process your personal information when you access or use our website, platform, products, and services (collectively, the \"Services\").",
    sections: [
      {
        h: "Scope",
        body: [
          "This Privacy Notice applies when you:",
          {
            list: [
              "Visit our website at https://ofinancial.markets",
              "Create or access an OFinancial Markets account",
              "Open or access a live trading account or demo account",
              "Deposit funds, withdraw funds, or place trades through any of our trading platforms (including MetaTrader 5)",
              "Contact us for support, marketing, or general enquiries",
              "Interact with us through forms, emails, advertising, or other communications",
            ],
          },
          "If you do not agree with this Privacy Notice, you must not use our Services.",
          "For any questions, contact: support@ofinancial.markets",
        ],
      },
      {
        h: "What Information Do We Collect?",
        body: [
          "We collect personal information that you provide to us, information collected automatically, and information obtained from third parties.",
          "Information You Provide",
          "The personal information you provide depends on how you interact with our Services and may include:",
          {
            list: [
              "Full name",
              "Email address",
              "Phone number",
              "Username and account credentials",
              "Billing and payment details",
              "Country of residence",
              "Identification or verification information",
              "Documents submitted for compliance or support",
              "Communications and correspondence",
            ],
          },
          "Payment Data",
          "Payments are processed through third-party payment providers. We do not store full card details. Payment providers handle payment data in accordance with their own privacy and security policies.",
          "Sensitive Data",
          "Where required, we may process identification, financial, or verification-related data for fraud prevention, compliance, or security purposes, in accordance with applicable laws.",
          "Automatically Collected Information",
          "When you use our Services, we may automatically collect:",
          {
            list: [
              "IP address",
              "Browser type and version",
              "Device type and operating system",
              "Usage data (pages visited, actions taken, time spent)",
              "Date and time of access",
              "Referring URLs",
              "Approximate location based on IP",
              "Cookies and tracking identifiers",
              "Error logs and system diagnostics",
            ],
          },
          "Information from Third Parties",
          "We may receive data from:",
          {
            list: [
              "Payment processors",
              "Analytics providers",
              "Marketing and advertising partners",
              "Affiliate and referral partners",
              "Fraud prevention and security providers",
              "Public databases and social platforms",
            ],
          },
        ],
      },
      {
        h: "How Do We Use Your Information?",
        body: [
          "We use your information to:",
          {
            list: [
              "Create and manage your account",
              "Provide access to our Services",
              "Process transactions, payments, and refunds",
              "Manage account operations, deposits, withdrawals, and platform functionality",
              "Communicate with you regarding your account or Services",
              "Provide customer support",
              "Improve platform performance and user experience",
              "Detect and prevent fraud, abuse, and unauthorised activity",
              "Enforce our Terms and Conditions",
              "Comply with legal and regulatory obligations",
            ],
          },
        ],
      },
      {
        h: "What Legal Bases Do We Rely On?",
        body: [
          "Where applicable under GDPR or similar laws, we rely on:",
          {
            list: [
              "Contractual necessity – to provide the Services you requested",
              "Consent – for marketing or optional features",
              "Legitimate interests – for business operations, analytics, security, and fraud prevention",
              "Legal obligations – to comply with laws and regulatory requirements",
            ],
          },
        ],
      },
      {
        h: "When and With Whom Do We Share Your Information?",
        body: [
          "We may share your personal information with:",
          {
            list: [
              "Payment processors",
              "Hosting and infrastructure providers",
              "Customer support systems",
              "Analytics and tracking providers",
              "Marketing and advertising platforms",
              "Affiliate and referral systems",
              "Fraud prevention and security services",
              "Professional advisers (legal, accounting)",
              "Authorities or regulators where required by law",
            ],
          },
          "We may also share or transfer your data as part of a merger, acquisition, restructuring, or sale of assets.",
        ],
      },
      {
        h: "Cookies and Tracking Technologies",
        body: [
          "We use cookies, pixels, and similar technologies to:",
          {
            list: [
              "Operate and maintain the website",
              "Remember user preferences",
              "Analyse traffic and behaviour",
              "Improve functionality",
              "Support marketing and remarketing",
            ],
          },
          "You can control cookies via your browser settings. Disabling cookies may affect functionality.",
        ],
      },
      {
        h: "Social Login Data",
        body: [
          "If you register or log in using third-party services (e.g. Google or similar), we may receive basic profile information such as your name and email address.",
          "We are not responsible for how those third parties handle your data.",
        ],
      },
      {
        h: "How Long Do We Keep Your Information?",
        body: [
          "We retain your personal information only as long as necessary to:",
          {
            list: [
              "Provide Services",
              "Comply with legal obligations",
              "Resolve disputes",
              "Prevent fraud",
              "Maintain business records",
            ],
          },
          "When no longer needed, data is deleted, anonymised, or securely stored until deletion is possible.",
        ],
      },
      {
        h: "How Do We Keep Your Information Safe?",
        body: [
          "We implement appropriate technical and organisational measures to protect your data.",
          "However, no system is completely secure, and we cannot guarantee absolute security. Use of the Services is at your own risk.",
        ],
      },
      {
        h: "Do We Collect Information from Minors?",
        body: [
          "Our Services are not intended for individuals under 18 years of age. We do not knowingly collect data from minors.",
        ],
      },
      {
        h: "What Are Your Privacy Rights?",
        body: [
          "Depending on your location, you may have rights to:",
          {
            list: [
              "Access your personal data",
              "Correct inaccurate data",
              "Request deletion",
              "Restrict processing",
              "Object to processing",
              "Request data portability",
              "Withdraw consent",
            ],
          },
          "To exercise your rights, contact: support@ofinancial.markets",
        ],
      },
      {
        h: "Marketing Communications",
        body: [
          "You may unsubscribe from marketing communications at any time.",
          "We may still send essential service-related communications.",
        ],
      },
      {
        h: "Account Information",
        body: [
          "You may request to update or delete your account by contacting us.",
          "Certain data may be retained where required by law or for legitimate business purposes.",
        ],
      },
      {
        h: "Do-Not-Track Features",
        body: [
          "We do not currently respond to Do-Not-Track signals due to lack of standardisation.",
        ],
      },
      {
        h: "International Data Transfers",
        body: [
          "Your information may be transferred and processed in countries outside your own.",
          "We take appropriate safeguards to protect your data in such cases.",
        ],
      },
      {
        h: "Region-Specific Rights",
        body: [
          "If you are located in jurisdictions such as the EEA, UK, or similar regions, you may have additional rights under applicable data protection laws.",
          "You may also lodge complaints with your local data protection authority if necessary.",
        ],
      },
      {
        h: "Updates to This Privacy Notice",
        body: [
          "We may update this Privacy Notice periodically.",
          "The updated version will be indicated by the \"Last updated\" date and will take effect upon publication.",
        ],
      },
      {
        h: "Contact Us",
        body: [
          "If you have any questions regarding this Privacy Notice:",
          {
            list: [
              "OFinancial Markets",
              "Website: https://ofinancial.markets",
              "Email: support@ofinancial.markets",
            ],
          },
        ],
      },
      {
        h: "Data Requests",
        body: [
          "To request access to, correction of, or deletion of your personal data, contact: support@ofinancial.markets",
        ],
      },
    ],
  },

  /* ────────────────────────── COOKIES ────────────────────────── */
  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    intro:
      "This Cookie Policy explains how OFinancial Markets (\"OFinancial Markets\", \"we\", \"us\", or \"our\") uses cookies and similar technologies when you visit our website https://ofinancial.markets (the \"Website\").",
    sections: [
      {
        h: "1. Introduction",
        body: [
          "When displaying content, features, advertisements, and other functionalities, we use technologies that collect and process certain information, including cookies.",
          "This Cookie Policy should be read alongside our Privacy Policy, which provides further details on how we process personal data.",
          "This policy does not apply to third-party websites that may be linked from our Website.",
          "By default, cookie settings on our Website allow the use of cookies. By continuing to use our Website, you agree to our use of cookies unless you adjust your settings.",
          "If you have any questions or wish to exercise your rights, you can contact us at: support@ofinancial.markets",
        ],
      },
      {
        h: "2. What Are Cookies and What Other Technologies Do We Use?",
        body: [
          "Cookies are small text files stored on your device when you visit a website. They are widely used to make websites function efficiently, improve user experience, and provide information to website owners.",
          "Cookies allow us to:",
          {
            list: [
              "Remember your preferences and settings",
              "Recognise your device and session",
              "Improve performance and usability",
              "Deliver relevant content and marketing",
              "Analyse website traffic and behaviour",
            ],
          },
          "Cookies may be set by OFinancial Markets (first-party cookies) or by third-party providers whose services we use.",
          "In addition to cookies, we may use:",
          {
            list: [
              "IP address tracking (a unique identifier assigned to your device)",
              "Pixels or web beacons",
              "Analytical scripts and tracking technologies",
              "Session storage and local storage",
            ],
          },
          "These technologies help us understand user behaviour and optimise our Services.",
        ],
      },
      {
        h: "3. Why Do We Use Cookies?",
        body: [
          "We use cookies to ensure the proper functionality of our Website and to enhance your overall experience.",
          "Cookies help us to:",
          {
            list: [
              "Maintain secure access to accounts",
              "Ensure the Website operates correctly",
              "Remember user preferences",
              "Improve website performance",
              "Analyse usage and behaviour",
              "Provide relevant marketing and advertising",
            ],
          },
          "We do not use cookies to directly identify you as an individual.",
          "Disabling cookies may reduce functionality and affect your experience on the Website.",
        ],
      },
      {
        h: "4. How to Manage Your Cookies",
        body: [
          "You can control and manage cookies through your browser settings.",
          "Most browsers allow you to:",
          {
            list: [
              "Block all cookies",
              "Delete existing cookies",
              "Receive alerts when cookies are being set",
            ],
          },
          "If you disable cookies, some features of the Website may not function properly.",
          "You can usually manage cookies through your browser settings:",
          {
            list: ["Google Chrome", "Mozilla Firefox", "Safari", "Microsoft Edge"],
          },
          "For more detailed instructions, refer to your browser's help section.",
        ],
      },
      {
        h: "5. What Types of Cookies Do We Use?",
        body: [
          "We use the following categories of cookies:",
          "Essential Cookies",
          "These cookies are necessary for the Website to function properly. They enable core features such as security, account access, and navigation.",
          "Without these cookies, the Website cannot operate correctly. These cookies are always active.",
          "Session Cookies",
          "Session cookies are temporary and are deleted when you close your browser.",
          "They allow us to:",
          {
            list: [
              "Maintain your session",
              "Remember information during navigation",
              "Ensure smooth operation between pages",
            ],
          },
          "Disabling these may negatively affect your browsing experience.",
          "Analytical Cookies",
          "These cookies help us understand how users interact with the Website.",
          "They allow us to:",
          {
            list: [
              "Measure traffic and usage",
              "Identify popular features",
              "Improve performance and usability",
            ],
          },
          "These cookies are optional.",
          "Functional Cookies",
          "Functional cookies remember your preferences and settings.",
          "They allow us to:",
          {
            list: [
              "Remember language preferences",
              "Save login or display settings",
              "Provide a more personalised experience",
            ],
          },
          "Third-Party Cookies",
          "These cookies are set by external providers, including:",
          {
            list: [
              "Analytics services",
              "Social media platforms",
              "Live chat systems",
              "Embedded content providers",
            ],
          },
          "They allow functionality such as content sharing and integrations.",
          "Disabling these cookies may limit certain features.",
          "Marketing Cookies",
          "Marketing cookies are used to deliver relevant advertisements.",
          "They may be set by OFinancial Markets or advertising partners to:",
          {
            list: [
              "Personalise advertising",
              "Measure campaign performance",
              "Track interactions across platforms",
            ],
          },
          "These cookies are only used where consent is provided.",
        ],
      },
      {
        h: "6. What Are Your Rights and How Can You Exercise Them?",
        body: [
          "You have the right to:",
          {
            list: [
              "Withdraw consent for non-essential cookies",
              "Control or delete cookies through your browser",
              "Request information about how your data is processed",
            ],
          },
          "You can manage cookies directly through your browser settings at any time.",
          "For further requests or questions, contact: support@ofinancial.markets",
        ],
      },
      {
        h: "7. Updates",
        body: [
          "We may update this Cookie Policy from time to time.",
          "Any changes will be published on this page with an updated revision date.",
          "We recommend reviewing this page periodically to stay informed.",
          "For more information on how we process personal data, please refer to our Privacy Policy.",
        ],
      },
    ],
  },

  /* ────────────────────────── REFUND ────────────────────────── */
  refund: {
    slug: "refund",
    title: "Refund Policy",
    intro:
      "The purchase fee for participation in OFinancial Markets services covers access to the simulated trading environment, evaluation systems, infrastructure, and administrative services.",
    sections: [
      {
        h: "1. Eligibility for refunds",
        body: [
          "This fee is non-refundable once access to the platform has been granted or trading activity has commenced.",
          "Refund eligibility is strictly subject to:",
          {
            list: [
              "full compliance with all programme rules",
              "successful identity verification where required",
              "adherence to all Terms and Conditions",
            ],
          },
          "Failure to meet any of the above conditions will result in ineligibility for any refund.",
        ],
      },
      {
        h: "2. Account Reset Fees",
        body: [
          "If a user fails an evaluation or chooses to reset their account, additional fees may apply.",
          "These fees are non-refundable, as they cover the setup and provisioning of a new simulated trading environment.",
        ],
      },
      {
        h: "3. Exceptional Circumstances",
        body: [
          "Refunds may only be considered in limited cases where a verified technical issue originating from OFinancial Markets systems has materially prevented participation in the evaluation.",
          "Requests must be submitted within seven days of the issue.",
          "All decisions are made at the sole discretion of the Company and are final.",
        ],
      },
      {
        h: "4. Account Termination and Disqualifications",
        body: [
          "No refund of any account-related fee will be issued where:",
          {
            list: [
              "the account has been suspended or terminated for a breach of our Client Agreement",
              "abusive, manipulative, or prohibited trading activity is detected (including latency arbitrage, market-data abuse, or use of unauthorised automated systems)",
              "identity verification or source-of-funds requirements are not met",
              "anti-money-laundering review is pending or has resulted in a denial of service",
            ],
          },
          "This applies to all account types.",
        ],
      },
      {
        h: "5. Platform and Service Fees",
        body: [
          "Fees paid for optional services (such as VPS subscriptions, premium analytics access, or any other paid feature) are non-refundable once the service period has commenced, except where required by applicable consumer-protection law.",
        ],
      },
      {
        h: "6. Chargebacks",
        body: [
          "Users agree not to initiate chargebacks after accessing the platform.",
          "In the event of a chargeback:",
          {
            list: [
              "all accounts will be immediately suspended",
              "access to the platform will be permanently revoked",
              "any gains or payouts may be withheld or reversed",
            ],
          },
          "OFinancial Markets reserves the right to recover any funds previously paid out and to pursue recovery of all associated costs, including administrative and legal expenses.",
        ],
      },
      {
        h: "7. Refund Processing",
        body: [
          "Approved refunds, where applicable, will be processed using the original payment method within two business days of approval.",
          "Processing times may vary depending on the payment provider.",
        ],
      },
      {
        h: "8. Changes or Updates of Policy",
        body: [
          "OFinancial Markets reserves the right to update or modify this policy at any time.",
          "Changes take effect immediately upon publication on the website.",
          "Continued use of the services constitutes acceptance of the updated policy.",
        ],
      },
    ],
  },
};

export const LEGAL_LAST_UPDATED = LAST_UPDATED;
